/**
 * The DriveApi for the web/desktop shell (one per app container): fetch against the API, or in mock mode the
 * in-browser store (code-split, loaded on first use).
 */
import type { DriveApi } from "@bundle/core";
import type { Container } from "../container.ts";
import { createDriveApi } from "./api.ts";

const byContainer = new WeakMap<Container, DriveApi>();

/** An object whose methods wait for `target` (the mock implementation loads lazily). */
function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    // Not a thenable: `then` stays undefined so the proxy is never mistaken for a promise.
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

export function driveApi(container: Container): DriveApi {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const api = container.mock
    ? deferred<DriveApi>(import("../mock/driveApi.ts").then(({ createMockDriveApi }) => createMockDriveApi(() => container.session.state.session?.token)))
    : createDriveApi(container.session);
  byContainer.set(container, api);
  return api;
}
